/**
 * Analytics Tracking
 *
 * Primary responsibilities:
 * - Define event tracking interfaces
 * - Provide tracking functions for client and server
 * - Maintain event name constants
 */

import { v4 as uuidv4 } from 'uuid'
import { ActivationEventNames } from './activation-event-contract'
import { MeaningfulOutcomeEventNames } from './meaningful-outcome-contract'

// Define allowed event names to ensure consistency
export const EventNames = {
  // User actions
  USER_SIGNUP: 'user_signup',
  USER_LOGIN: 'user_login',
  USER_LOGOUT: 'user_logout',
  SIGNUP: 'signup', // Alias for USER_SIGNUP
  LOGIN: 'login', // Alias for USER_LOGIN
  LOGOUT: 'logout', // Alias for USER_LOGOUT

  // Feature flags
  FEATURE_FLAG_IMPRESSION: 'feature_flag_impression',
  FEATURE_FLAG_CONVERSION: 'feature_flag_conversion',

  // Chat actions
  HOMEPAGE_CHAT_SUBMITTED: 'homepage_chat_submitted',
  HOMEPAGE_CHAT_RESPONSE_SHOWN: 'homepage_chat_response_shown',
  HOMEPAGE_CTA_CLICKED: 'homepage_cta_clicked',
  CHAT_MESSAGE_SENT: 'chat_message_sent',
  FIRST_CHAT_MESSAGE_SENT: 'first_chat_message_sent',
  CHAT_MESSAGE_RECEIVED: 'chat_message_received',
  CHAT_STARTED: 'chat_started',
  CHAT_ENDED: 'chat_ended',
  MESSAGE_SENT: 'message_sent', // Alias for CHAT_MESSAGE_SENT

  // UI events
  BUTTON_CLICK: 'button_click',
  LINK_CLICK: 'link_click',
  PAGE_VIEW: 'page_view',

  // Settings events
  SETTINGS_UPDATED: 'settings_updated',
  MODEL_SELECTED: 'model_selected',
  THEME_CHANGED: 'theme_changed',

  // Tool related events
  TOOL_SELECTED: 'tool_selected',
  TOOL_EXECUTED: 'tool_executed',
  TOOL_ERROR: 'tool_error',

  // Payment and subscription events
  PAYMENT_COMPLETED: 'payment_completed',
  SUBSCRIPTION_STARTED: 'subscription_started',
  SUBSCRIPTION_CANCELED: 'subscription_canceled',
  SUBSCRIPTION_UPGRADED: 'subscription_upgraded',
  SUBSCRIPTION_DOWNGRADED: 'subscription_downgraded',

  // Authentication events
  PASSWORD_RESET_REQUEST: 'password_reset_request',

  // Error events
  ERROR_OCCURRED: 'error_occurred',

  // Custom events for examples
  EXAMPLE_BUTTON_CLICK: 'example_button_click',
  EXAMPLE_SERVER_ACTION: 'example_server_action',

  // Nexus Funnel Events
  NEXUS_LANDING_VIEWED: 'nexus_landing_viewed',
  NEXUS_DEMO_STARTED: 'nexus_demo_started',
  NEXUS_DEMO_COMPLETED: 'nexus_demo_completed',
  NEXUS_SIGNUP_STARTED: 'nexus_signup_started',
  NEXUS_SIGNUP_COMPLETED: 'nexus_signup_completed',
  NEXUS_ONBOARDING_STARTED: 'nexus_onboarding_started',
  NEXUS_ONBOARDING_STEP_COMPLETED: 'nexus_onboarding_step_completed',
  NEXUS_ONBOARDING_COMPLETED: 'nexus_onboarding_completed',
  NEXUS_PRICING_VIEWED: 'nexus_pricing_viewed',
  NEXUS_PLAN_SELECTED: 'nexus_plan_selected',
  NEXUS_CHECKOUT_STARTED: 'nexus_checkout_started',
  NEXUS_CHECKOUT_COMPLETED: 'nexus_checkout_completed',
  NEXUS_TRIAL_STARTED: 'nexus_trial_started',
  NEXUS_TRIAL_EXPIRING: 'nexus_trial_expiring',
  NEXUS_TRIAL_CONVERTED: 'nexus_trial_converted',
  NEXUS_TRIAL_EXPIRED: 'nexus_trial_expired',
  NEXUS_PAYMENT_MODAL_SHOWN: 'nexus_payment_modal_shown',
  NEXUS_PAYMENT_MODAL_CTA_CLICKED: 'nexus_payment_modal_cta_clicked',
  NEXUS_PAYMENT_MODAL_DISMISSED: 'nexus_payment_modal_dismissed',

  // Activation Funnel Events
  ...ActivationEventNames,

  // Meaningful Outcome Events
  ...MeaningfulOutcomeEventNames
} as const

// TypeScript type for event names
export type EventName = keyof typeof EventNames | string

// Base properties all events should have
interface BaseEventProps {
  userId?: string
  sessionId?: string
  session_id?: string
  timestamp?: number
  url?: string
  referrer?: string
}

type TrackResponsePayload = Record<string, unknown> | null

export type TrackResponse = {
  ok: boolean
  payload: TrackResponsePayload
}

const ANALYTICS_DEBUG_DENIED_KEY_PARTS = [
  'content',
  'email',
  'message',
  'payment',
  'phone',
  'prompt',
  'secret',
  'text',
  'token'
]

function shouldLogAnalyticsDebug() {
  return (
    process.env.NODE_ENV === 'development' &&
    process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === 'true'
  )
}

function sanitizeDebugValue(key: string, value: unknown): unknown {
  const normalizedKey = key.toLowerCase()

  if (ANALYTICS_DEBUG_DENIED_KEY_PARTS.some(part => normalizedKey.includes(part))) {
    return undefined
  }

  if (key === 'url' || key === 'referrer') {
    if (typeof value !== 'string') return undefined

    try {
      return new URL(value).pathname
    } catch {
      return undefined
    }
  }

  if (value === null || typeof value === 'boolean' || typeof value === 'number') {
    return value
  }

  if (typeof value === 'string') {
    return value.slice(0, 160)
  }

  return undefined
}

function sanitizeDebugProperties(properties: Record<string, unknown>) {
  const safeProperties: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(properties)) {
    const safeValue = sanitizeDebugValue(key, value)
    if (safeValue !== undefined) {
      safeProperties[key] = safeValue
    }
  }

  return safeProperties
}

function logAnalyticsDebugEvent(
  eventName: string,
  properties: Record<string, unknown>,
  status: 'emit' | 'failed' | 'skipped'
) {
  if (!shouldLogAnalyticsDebug()) return

  console.debug('[analytics]', status, eventName, sanitizeDebugProperties(properties))
}

async function readTrackResponsePayload(
  response: Response
): Promise<TrackResponsePayload> {
  if (typeof response.json !== 'function') {
    return null
  }

  const payload = await response.json().catch(() => null)

  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null
  }

  return payload as Record<string, unknown>
}

/**
 * Track an event and return the analytics route response.
 */
export async function trackWithResponse<T extends Record<string, unknown>>(
  eventName: EventName,
  properties?: T & BaseEventProps
): Promise<TrackResponse> {
  try {
    // Generate default properties
    const sessionId = getOrCreateSessionId()
    const defaultProps: BaseEventProps = {
      timestamp: Date.now(),
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
      sessionId,
      session_id: sessionId
    }
    const eventProperties = {
      ...defaultProps,
      ...properties
    }

    logAnalyticsDebugEvent(String(eventName), eventProperties, 'emit')

    // Send to the analytics API
    const response = await fetch('/api/analytics/track', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        event: eventName,
        properties: eventProperties
      })
    })
    const payload = await readTrackResponsePayload(response)

    if (!response.ok) {
      logAnalyticsDebugEvent(String(eventName), eventProperties, 'failed')
      return {
        ok: false,
        payload
      }
    }

    if (payload?.captured === false) {
      logAnalyticsDebugEvent(String(eventName), eventProperties, 'skipped')
      return {
        ok: false,
        payload
      }
    }

    return {
      ok: true,
      payload
    }
  } catch (error) {
    // Don't let analytics errors affect the application
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to track event:', error)
    }
    return {
      ok: false,
      payload: null
    }
  }
}

/**
 * Track an event on the client side
 */
export async function track<T extends Record<string, unknown>>(
  eventName: EventName,
  properties?: T & BaseEventProps
) {
  const result = await trackWithResponse(eventName, properties)

  return result.ok
}

/**
 * Track an event on the server side
 */
export async function trackServer<T extends Record<string, unknown>>(
  eventName: EventName,
  properties?: T & BaseEventProps
) {
  try {
    if (typeof window !== 'undefined') {
      return track(eventName, properties)
    }

    // Generate default properties
    const defaultProps: BaseEventProps = {
      timestamp: Date.now()
    }
    const eventProperties = {
      ...defaultProps,
      ...properties
    }
    const captured = await captureServerTrackEvent(String(eventName), eventProperties)

    if (!captured) {
      logAnalyticsDebugEvent(String(eventName), eventProperties, 'skipped')
    }

    return captured
  } catch (error) {
    // Don't let analytics errors affect the application
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to track server event:', error)
    }
    return false
  }
}

function readServerString(value: unknown) {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

async function captureServerTrackEvent(
  eventName: string,
  properties: Record<string, unknown>
) {
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com'

  if (!key) {
    return false
  }

  const { sanitizeProductAnalyticsProperties } = await import(
    '@/lib/analytics/product-events'
  )
  const userId =
    readServerString(properties.user_id) || readServerString(properties.userId)
  const sanitizedProperties = sanitizeProductAnalyticsProperties(
    eventName,
    properties,
    userId
  )

  if (!sanitizedProperties) {
    return false
  }

  const distinctId =
    userId ||
    readServerString(sanitizedProperties.session_id) ||
    readServerString(properties.session_id) ||
    readServerString(properties.sessionId)

  if (!distinctId) {
    return false
  }

  const response = await fetch(`${host.replace(/\/$/, '')}/capture/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      api_key: key,
      event: eventName,
      distinct_id: distinctId,
      properties: sanitizedProperties
    })
  })

  return response.ok
}

/**
 * Get or create a session ID for tracking
 */
export function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') {
    return uuidv4()
  }

  // Try to get from sessionStorage
  let sessionId = sessionStorage.getItem('analytics_session_id')

  // If not found, create a new one
  if (!sessionId) {
    sessionId = uuidv4()
    sessionStorage.setItem('analytics_session_id', sessionId)
  }

  return sessionId
}
